/**
 * The sample editor.
 *
 * Two kinds of operation live here and the difference matters:
 *
 * - **Parameter edits** — trim, gain, pitch, speed, fades, loop points, the effect rack.
 *   These write to `SampleEdit`. The source file is never touched, so undo is exact and
 *   A/B against the original is a single boolean.
 * - **Structural edits** — crop, split, slice, duplicate a region, delete a region. These
 *   need audio that does not exist yet, so they render once and create a *new* asset. The
 *   original stays in the library, which is what makes "delete a region" recoverable.
 */

import { h, onCleanup } from '../../core/dom';
import { computed, effect, signal } from '../../core/signal';
import { icon, iconButton, neonButton, parameterRow, segmentedRail, toggle, valueStepper } from '../components/controls';
import { processingOverlay } from '../components/sheet';
import { waveformView, type TimeRange, type WaveformViewHandle } from '../components/waveform';
import { concatenateRenders, type RenderedAudio } from '../../audio/offline-render';
import {
  EFFECT_KINDS,
  FADE_CURVES,
  PATTERN_LENGTH_OPTIONS,
  effectKindDisplayName,
  effectKindParameters,
  effectKindSymbolName,
  fadeCurveDisplayName,
  makeEffectNode,
  makePattern,
  makeTrack,
  padIsEmpty,
  projectAsset,
  type EffectNode,
  type AmpEnvelope,
  type FadeCurve,
  type SampleAsset,
  type SampleEdit,
} from '../../model/types';
import {
  analysis,
  assignAssetToPad,
  bufferForAsset,
  currentPatternID,
  engine,
  offStepValue,
  mutate,
  notify,
  project,
  renderEdit,
  reportFailure,
  revision,
  saveRenderedAsAsset,
  selectedAssetID,
  selectedPadID,
  stopPadForEditing,
} from '../../app/state';
import { adoptEditorSurface } from '../../app/transitions';
import { historyButtons, presentMenu, requiresProject, screenBody, screenHeader, section, formatPreciseTime } from '../../app/shell';

/**
 * The editor edits the pad's copy when it was opened from a pad, and the asset's default
 * edit otherwise — the same rule as the native build, so a pad's tweaks never leak back on
 * to every other pad using the same sound.
 */
interface EditTarget {
  asset: SampleAsset;
  /** The pad this editor was opened from, when it was opened from one at all. */
  padID?: string;
  read: () => SampleEdit | undefined;
  write: (label: string, change: (edit: SampleEdit) => void, coalesceKey?: string) => void;
}

function resolveTarget(assetID: string | undefined): EditTarget | null {
  const open = project.peek();
  if (open === null) return null;
  const padID = selectedPadID.peek();
  const pad = padID === null ? undefined : open.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === padID);
  const id = assetID ?? pad?.assetID ?? selectedAssetID.peek() ?? open.assets[0]?.id;
  if (id === undefined) return null;
  const asset = projectAsset(open, id);
  if (asset === undefined) return null;

  if (pad !== undefined && pad.assetID === id) {
    return {
      asset,
      padID: pad.id,
      read: () => project.peek()?.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === pad.id)?.edit,
      write: (label, change, coalesceKey) =>
        mutate(
          label,
          (target) => {
            const live = target.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === pad.id);
            if (live !== undefined) change(live.edit);
          },
          coalesceKey === undefined ? {} : { coalesceKey },
        ),
    };
  }
  return {
    asset,
    read: () => project.peek()?.assets.find((entry) => entry.id === id)?.defaultEdit,
    write: (label, change, coalesceKey) =>
      mutate(
        label,
        (target) => {
          const live = target.assets.find((entry) => entry.id === id);
          if (live !== undefined) change(live.defaultEdit);
        },
        coalesceKey === undefined ? {} : { coalesceKey },
      ),
  };
}

export function waveEditorScreen(context: { assetID?: string } = {}): HTMLElement {
  if (project.peek() === null) return requiresProject();
  const target = resolveTarget(context.assetID);
  if (target === null) {
    return h(
      'div',
      { class: 'screen screen--wave' },
      screenHeader({ title: 'Sample editor' }),
      screenBody(h('p', { class: 'muted' }, 'Open a sound from the library or a pad and it appears here.')),
    );
  }

  // Opening an editor for a sound stops that sound. Auditioning a trim against the same pad
  // still looping on the board underneath is not an audition, and the board offers no way to
  // stop it from here: the tile is gone and Panic is on the screen the user just left.
  if (target.padID !== undefined) stopPadForEditing(target.padID);

  // Aliased after the null check: `rackRow` is a hoisted declaration, so it cannot see the
  // narrowing applied to `target` above.
  const edits: EditTarget = target;
  const asset = target.asset;
  const edit = computed(() => {
    revision.value;
    return target.read();
  });
  const selection = signal<TimeRange | null>(null);
  const buffer = signal<AudioBuffer | null>(null);
  const compareOriginal = signal(false);
  const busy = signal(false);

  const view: WaveformViewHandle = waveformView({
    interaction: 'select',
    allowsZoom: true,
    showsTimeRuler: true,
    tint: 'var(--accent-magenta)',
    label: `Waveform for ${asset.name}`,
    onSelectionChanged: (range) => {
      selection.value = range;
    },
    onScrub: (time) => {
      const decoded = buffer.peek();
      if (decoded === null) return;
      // Short grains while the finger is down: long enough to identify what is under it,
      // short enough that dragging sounds like scrubbing rather than like a queue of clips.
      engine.previewBuffer(decoded, { offset: time, duration: Math.min(0.2, Math.max(0.02, decoded.duration - time)) });
    },
    onScrubEnded: () => engine.stopPreview(),
  });

  void (async (): Promise<void> => {
    const decoded = await bufferForAsset(asset);
    if (decoded === null) {
      reportFailure('That sound is missing', new Error('Its file could not be found on this device.'));
      return;
    }
    buffer.value = decoded;
    try {
      view.setPeaks(await analysis.peaks(decoded, 240));
      view.zoomToFit();
    } catch (error) {
      reportFailure('Waveform analysis failed', error);
    }
  })();

  const stopOverlay = effect(() => {
    const current = edit.value;
    if (current === undefined) return;
    view.setFades(current.fadeInDuration, current.fadeOutDuration, current.fadeCurve);
    view.setLoopRange(
      current.loopEnabled && current.loopStart !== undefined && current.loopEnd !== undefined
        ? { start: current.loopStart, end: current.loopEnd }
        : null,
    );
    view.setSliceMarkers(asset.sliceMarkers);
  });

  const stopPlayhead = effect(() => {
    engine.meterTick.value;
    engine.activity.value;
    view.setPlayhead(engine.isPreviewing() ? engine.previewProgress() * asset.duration : null);
  });

  // --- Helpers ------------------------------------------------------------------------

  const currentEdit = (): SampleEdit | undefined => edit.peek();

  /** Nearest zero crossing within 12 ms — enough to remove a click, small enough to be exact. */
  function snapToZero(time: number): number {
    const decoded = buffer.peek();
    if (decoded === null) return time;
    const data = decoded.getChannelData(0);
    const centre = Math.round(time * decoded.sampleRate);
    const window = Math.round(decoded.sampleRate * 0.012);
    let best = centre;
    let bestValue = Math.abs(data[centre] ?? 0);
    for (let offset = 1; offset < window; offset++) {
      for (const index of [centre - offset, centre + offset]) {
        if (index < 1 || index >= data.length) continue;
        const value = Math.abs(data[index]!);
        const crossing = (data[index - 1]! < 0) !== (data[index]! < 0);
        if (crossing && value < bestValue) {
          best = index;
          bestValue = value;
        }
      }
    }
    return best / decoded.sampleRate;
  }

  const snapEnabled = signal(true);
  const place = (time: number): number => (snapEnabled.peek() ? snapToZero(time) : time);

  async function renderCurrent(): Promise<AudioBuffer | null> {
    const decoded = buffer.peek();
    const current = currentEdit();
    if (decoded === null || current === undefined) return null;
    const rendered = await renderEdit(decoded, current, { tempo: project.peek()?.tempo ?? 96, sourceName: asset.name });
    const ctx = engine.ensureContext();
    const out = ctx.createBuffer(rendered.channels.length, rendered.channels[0]?.length ?? 1, rendered.sampleRate);
    for (let index = 0; index < rendered.channels.length; index++) {
      out.copyToChannel(new Float32Array(rendered.channels[index]!), index);
    }
    return out;
  }

  /** Renders a region to a brand-new asset. The original is never modified. */
  async function deriveAsset(
    name: string,
    transform: (edit: SampleEdit) => SampleEdit,
    onCreated?: (created: SampleAsset) => void,
  ): Promise<void> {
    const decoded = buffer.peek();
    const current = currentEdit();
    if (decoded === null || current === undefined || busy.peek()) return;
    busy.value = true;
    const overlay = processingOverlay({ title: `Rendering “${name}”…`, message: 'The original stays untouched in your library.' });
    overlay.present();
    try {
      const rendered = await renderEdit(decoded, transform(structuredClone(current)), {
        tempo: project.peek()?.tempo ?? 96,
        sourceName: asset.name,
      });
      const created = await saveRenderedAsAsset(rendered, name, 'renderedBounce', ['edited']);
      if (onCreated === undefined) notify('Created', `“${created.name}” is in your library.`);
      else onCreated(created);
    } catch (error) {
      reportFailure('That edit could not be rendered', error);
    } finally {
      overlay.destroy();
      busy.value = false;
    }
  }

  /** Milliseconds of ramp either side of a splice. Long enough to kill the step, short
   *  enough that nothing musical is lost at the join. */
  const SEAM_SECONDS = 0.004;

  /**
   * Deletes a region and closes the gap — the operation the button has always been named
   * after.
   *
   * What it used to do was push the selection into `silencedRegions`, which is what the
   * Silence button does: `silenceRegion` zero-fills in place and preserves the buffer's
   * length, so "Delete" left a hole of digital silence exactly as long as the audio it had
   * supposedly removed. Closing a gap needs new audio, so the two surviving regions are
   * rendered through the same edit and concatenated, with a short ramp either side of the
   * seam so the join does not click.
   */
  async function deleteRegion(range: TimeRange): Promise<void> {
    const decoded = buffer.peek();
    const current = currentEdit();
    if (decoded === null || current === undefined || busy.peek()) return;

    const regionStart = current.startTime;
    const regionEnd = current.endTime ?? asset.duration;
    const cutFrom = Math.max(regionStart, place(range.start));
    const cutTo = Math.min(regionEnd, place(range.end));
    const parts: Array<{ start: number; end: number }> = [];
    if (cutFrom - regionStart > 0.001) parts.push({ start: regionStart, end: cutFrom });
    if (regionEnd - cutTo > 0.001) parts.push({ start: cutTo, end: regionEnd });
    if (parts.length === 0) {
      notify('Nothing would be left', 'That selection covers the whole sound. Trim it instead.');
      return;
    }

    busy.value = true;
    const name = `${asset.name} edit`;
    const overlay = processingOverlay({ title: `Rendering “${name}”…`, message: 'The original stays untouched in your library.' });
    overlay.present();
    try {
      const rendered: RenderedAudio[] = [];
      for (const part of parts) {
        const draft = structuredClone(current);
        draft.startTime = part.start;
        draft.endTime = part.end;
        // The chain is applied to each surviving piece, so a reverb tail does not get cut in
        // half by a join it never knew about.
        rendered.push(await renderEdit(decoded, draft, { tempo: project.peek()?.tempo ?? 96, sourceName: asset.name }));
      }
      const joined = concatenateRenders(rendered, SEAM_SECONDS);
      const created = await saveRenderedAsAsset(joined, name, 'renderedBounce', ['edited']);
      notify('Created', `“${created.name}” is in your library, with the selection removed.`);
    } catch (error) {
      reportFailure('That edit could not be rendered', error);
    } finally {
      overlay.destroy();
      busy.value = false;
    }
  }

  /**
   * Repeats a region in place, inside the sample.
   *
   * "Duplicate region" and "Repeat" are both named as operations in their own right, and
   * neither existed: the button called Duplicate rendered the selection to a *new asset* and
   * dropped it on a pad, which is "crop, then assign" under another name, and nothing
   * repeated anything. This inserts `copies` extra passes of the selection immediately after
   * itself and keeps everything either side, which is the operation a loop is built from.
   */
  async function repeatRegion(range: TimeRange, copies: number): Promise<void> {
    const decoded = buffer.peek();
    const current = currentEdit();
    if (decoded === null || current === undefined || busy.peek()) return;

    const regionStart = current.startTime;
    const regionEnd = current.endTime ?? asset.duration;
    const from = Math.max(regionStart, place(range.start));
    const to = Math.min(regionEnd, place(range.end));
    if (to - from < 0.005) {
      notify('That selection is too short', 'Drag a longer region to repeat.');
      return;
    }

    const parts: Array<{ start: number; end: number }> = [];
    if (from - regionStart > 0.001) parts.push({ start: regionStart, end: from });
    for (let index = 0; index < copies + 1; index++) parts.push({ start: from, end: to });
    if (regionEnd - to > 0.001) parts.push({ start: to, end: regionEnd });

    busy.value = true;
    const name = `${asset.name} repeat`;
    const overlay = processingOverlay({ title: `Rendering “${name}”…`, message: 'The original stays untouched in your library.' });
    overlay.present();
    try {
      const rendered: RenderedAudio[] = [];
      for (const part of parts) {
        const draft = structuredClone(current);
        draft.startTime = part.start;
        draft.endTime = part.end;
        rendered.push(await renderEdit(decoded, draft, { tempo: project.peek()?.tempo ?? 96, sourceName: asset.name }));
      }
      const joined = concatenateRenders(rendered, SEAM_SECONDS);
      const created = await saveRenderedAsAsset(joined, name, 'renderedBounce', ['edited']);
      notify('Created', `“${created.name}” repeats that region ${copies + 1} times.`);
    } catch (error) {
      reportFailure('That edit could not be rendered', error);
    } finally {
      overlay.destroy();
      busy.value = false;
    }
  }

  // --- Region operations --------------------------------------------------------------

  const regionActions = h(
    'div',
    { class: 'wave-actions' },
    neonButton({
      title: 'Trim to selection',
      style: 'secondary',
      enabled: computed(() => selection.value !== null),
      onPress: () => {
        const range = selection.peek();
        if (range === null) return;
        target.write('Trim', (draft) => {
          draft.startTime = place(range.start);
          draft.endTime = place(range.end);
        });
        notify('Trimmed', 'Non-destructive. The file is unchanged.');
      },
    }).el,
    neonButton({
      title: 'Crop to a new sound',
      style: 'secondary',
      enabled: computed(() => selection.value !== null),
      onPress: () => {
        const range = selection.peek();
        if (range === null) return;
        void deriveAsset(`${asset.name} crop`, (draft) => {
          draft.startTime = place(range.start);
          draft.endTime = place(range.end);
          return draft;
        });
      },
    }).el,
    neonButton({
      title: 'Split here',
      style: 'secondary',
      enabled: computed(() => selection.value !== null),
      onPress: () => {
        const range = selection.peek();
        const current = currentEdit();
        if (range === null || current === undefined) return;
        const point = place(range.start);
        void (async (): Promise<void> => {
          await deriveAsset(`${asset.name} A`, (draft) => {
            draft.endTime = point;
            return draft;
          });
          await deriveAsset(`${asset.name} B`, (draft) => {
            draft.startTime = point;
            return draft;
          });
        })();
      },
    }).el,
    neonButton({
      title: 'Repeat selection',
      style: 'secondary',
      enabled: computed(() => selection.value !== null && !busy.value),
      onPress: () => {
        const range = selection.peek();
        if (range === null) return;
        presentMenu('Repeat this region', [1, 2, 3, 7].map((copies) => ({
          title: `${copies + 1}×`,
          detail: copies === 1 ? 'Doubles it' : `Plays it ${copies + 1} times in a row`,
          onSelect: () => void repeatRegion(range, copies),
        })));
      },
    }).el,
    neonButton({
      title: 'Duplicate to a pad',
      style: 'secondary',
      enabled: computed(() => selection.value !== null),
      onPress: () => {
        const range = selection.peek();
        if (range === null) return;
        // What separated this from "Crop to a new sound" used to be nothing at all: both
        // derived an asset over the selection and the only difference was zero-crossing
        // snapping, so "Duplicate" duplicated nothing. It does what the native editor's
        // Duplicate does — the region lands on the next free pad, ready to play.
        void deriveAsset(
          `${asset.name} copy`,
          (draft) => {
            draft.startTime = range.start;
            draft.endTime = range.end;
            return draft;
          },
          (created) => {
            const open = project.peek();
            const free = open?.banks
              .flatMap((bank) => bank.pads)
              .sort((a, b) => a.slot - b.slot)
              .find((pad) => padIsEmpty(pad));
            if (free === undefined) {
              notify('Created', `“${created.name}” is in your library. Every pad is full, so nothing was replaced.`);
              return;
            }
            assignAssetToPad(free.id, created.id, 'Duplicate to Pad');
            notify('On a pad', `“${created.name}” → pad ${free.slot + 1}`);
          },
        );
      },
    }).el,
    neonButton({
      title: 'Silence selection',
      style: 'secondary',
      enabled: computed(() => selection.value !== null),
      onPress: () => {
        const range = selection.peek();
        if (range === null) return;
        target.write('Silence Region', (draft) => {
          draft.silencedRegions.push({ lowerBound: range.start, upperBound: range.end });
        });
      },
    }).el,
    neonButton({
      title: 'Delete selection',
      style: 'destructive',
      enabled: computed(() => selection.value !== null),
      onPress: () => {
        const range = selection.peek();
        if (range === null) return;
        // Closing the gap needs new audio, so it produces a new sound rather than editing
        // the original in place — which is what makes this undoable in every sense.
        void deleteRegion(range);
      },
    }).el,
    neonButton({
      title: 'Clear selection',
      style: 'ghost',
      onPress: () => {
        selection.value = null;
        view.setSelection(null);
      },
    }).el,
  );

  const sliceActions = h(
    'div',
    { class: 'wave-actions' },
    neonButton({
      title: 'Slice by transient',
      style: 'secondary',
      onPress: () => {
        const decoded = buffer.peek();
        if (decoded === null) return;
        void (async (): Promise<void> => {
          // Analysis runs in a worker that can fail to load. Slicing degrades to a clear
          // message rather than an unhandled rejection that leaves the sheet half-open.
          const onsets = await analysis.transients(decoded, 0.5).catch(() => [] as number[]);
          if (onsets.length === 0) {
            notify('No transients found', 'Try slicing evenly instead.');
            return;
          }
          mutate('Slice by Transient', (open) => {
            const live = open.assets.find((entry) => entry.id === asset.id);
            if (live !== undefined) live.sliceMarkers = onsets;
          });
          view.setSliceMarkers(onsets);
          notify(`${onsets.length} slices`, 'Markers are shown on the waveform.');
        })();
      },
    }).el,
    neonButton({
      title: 'Slice evenly',
      style: 'secondary',
      onPress: () =>
        presentMenu(
          'How many slices?',
          [4, 8, 16, 32].map((count) => ({
            title: `${count} slices`,
            onSelect: () => {
              const markers = Array.from({ length: count }, (_unused, index) => (asset.duration * index) / count);
              mutate('Slice Evenly', (open) => {
                const live = open.assets.find((entry) => entry.id === asset.id);
                if (live !== undefined) live.sliceMarkers = markers;
              });
              view.setSliceMarkers(markers);
            },
          })),
        ),
    }).el,
    neonButton({
      title: 'Slice by division',
      style: 'secondary',
      onPress: () => {
        const tempo = project.peek()?.tempo ?? 96;
        presentMenu(
          `Slice at ${Math.round(tempo)} BPM`,
          [
            { title: '1/4 notes', beats: 1 },
            { title: '1/8 notes', beats: 0.5 },
            { title: '1/16 notes', beats: 0.25 },
          ].map((option) => ({
            title: option.title,
            onSelect: () => {
              const step = (60 / tempo) * option.beats;
              const markers: number[] = [];
              for (let time = 0; time < asset.duration; time += step) markers.push(time);
              mutate('Slice by Division', (open) => {
                const live = open.assets.find((entry) => entry.id === asset.id);
                if (live !== undefined) live.sliceMarkers = markers;
              });
              view.setSliceMarkers(markers);
            },
          })),
        );
      },
    }).el,
    neonButton({
      title: 'Slices to pads',
      style: 'primary',
      enabled: computed(() => {
        revision.value;
        return (projectAsset(project.value!, asset.id)?.sliceMarkers.length ?? 0) > 0;
      }),
      onPress: () => {
        const open = project.peek();
        if (open === null) return;
        const markers = projectAsset(open, asset.id)?.sliceMarkers ?? [];
        // Counted before the write, and across every bank rather than only the first: with a
        // full bank 0 this laid out nothing, pushed an undo entry for the no-op, and still
        // said "Each slice is on its own pad."
        const free = open.banks
          .flatMap((bank) => bank.pads)
          .filter((pad) => padIsEmpty(pad))
          .sort((a, b) => a.slot - b.slot);
        const placing = Math.min(markers.length, free.length);
        if (placing === 0) {
          notify('No free pads', 'Clear a pad, or add a bank, and try again. Nothing was changed.');
          return;
        }
        const targetIDs = free.slice(0, placing).map((pad) => pad.id);
        mutate('Slices to Pads', (draft) => {
          const pads = draft.banks.flatMap((bank) => bank.pads);
          targetIDs.forEach((padID, index) => {
            const pad = pads.find((entry) => entry.id === padID);
            if (pad === undefined) return;
            pad.assetID = asset.id;
            pad.name = `${asset.name} ${index + 1}`;
            pad.edit = structuredClone(asset.defaultEdit);
            pad.edit.startTime = markers[index]!;
            pad.edit.endTime = markers[index + 1] ?? asset.duration;
          });
        });
        notify(
          `${placing} slice${placing === 1 ? '' : 's'} laid out`,
          placing < markers.length
            ? `${markers.length - placing} did not fit. There were only ${placing} free pads.`
            : 'Each slice is on its own pad.',
        );
      },
    }).el,
    neonButton({
      title: 'Slices to a pattern',
      style: 'secondary',
      enabled: computed(() => {
        revision.value;
        return (projectAsset(project.value!, asset.id)?.sliceMarkers.length ?? 0) > 0;
      }),
      onPress: () => slicesToPattern(),
    }).el,
  );

  /**
   * Builds a pattern that replays the slices in order.
   *
   * "Slice a loop into pads, rearrange slices, reverse slices, **and create new patterns from
   * them**" — the last clause had no implementation: slicing put the pieces on pads and
   * stopped, leaving the user to rebuild the original rhythm by hand in the sequencer. This
   * lays each slice on its own track, one step apart, so the new pattern plays back the loop
   * it came from and every piece is then free to be moved.
   */
  function slicesToPattern(): void {
    const open = project.peek();
    if (open === null) return;
    const markers = projectAsset(open, asset.id)?.sliceMarkers ?? [];
    if (markers.length === 0) return;

    const pads = open.banks.flatMap((bank) => bank.pads);
    // Only pads this asset was actually sliced onto, in slice order.
    const slicePads = pads
      .filter((pad) => pad.assetID === asset.id && markers.includes(pad.edit.startTime))
      .sort((left, right) => left.edit.startTime - right.edit.startTime);
    if (slicePads.length === 0) {
      notify('Lay the slices out first', 'Use “Slices to pads”, then build a pattern from them.');
      return;
    }

    // One step per slice, rounded up to a musical length so the pattern loops sensibly.
    const wanted = slicePads.length;
    const length = PATTERN_LENGTH_OPTIONS.find((option) => option >= wanted) ?? PATTERN_LENGTH_OPTIONS[PATTERN_LENGTH_OPTIONS.length - 1] ?? 16;
    // Not "slices": the loop slicer's bank already wore that word, and a bank called
    // "Boom Bap 80 Slices" sitting beside a pattern called "Boom Bap 80 slices" is the same
    // collision with a lowercase s. This pattern lays the existing slice pads out one step
    // apart, so it is named for the steps.
    const created = makePattern(`${asset.name} Steps`, { length, stepsPerBeat: 4 });
    for (let index = 0; index < slicePads.length; index++) {
      const pad = slicePads[index]!;
      const track = makeTrack({ padID: pad.id, name: pad.name, length, color: pad.color });
      while (track.steps.length < length) track.steps.push(offStepValue());
      const step = track.steps[index % length];
      if (step !== undefined) track.steps[index % length] = { ...step, isActive: true, velocity: 0.9 };
      created.tracks.push(track);
    }

    mutate('Pattern From Slices', (draft) => {
      draft.patterns.push(created);
    });
    currentPatternID.value = created.id;
    notify('Pattern created', `“${created.name}” plays the ${slicePads.length} slices in order.`);
  }

  // --- Parameters ----------------------------------------------------------------------

  const gain = valueStepper({
    label: 'Gain',
    value: computed(() => edit.value?.gainDB ?? 0),
    min: -30,
    max: 18,
    step: 0.5,
    unit: 'dB',
    onChange: (value) => target.write('Gain', (draft) => (draft.gainDB = value), 'gain'),
  });
  const pan = valueStepper({
    label: 'Pan',
    value: computed(() => edit.value?.pan ?? 0),
    min: -1,
    max: 1,
    step: 0.05,
    format: (value) => (value === 0 ? 'Centre' : value < 0 ? `${Math.round(-value * 100)}% L` : `${Math.round(value * 100)}% R`),
    onChange: (value) => target.write('Pan', (draft) => (draft.pan = value), 'pan'),
  });
  const semitones = valueStepper({
    label: 'Pitch',
    value: computed(() => edit.value?.pitchSemitones ?? 0),
    min: -24,
    max: 24,
    unit: 'st',
    onChange: (value) => target.write('Pitch', (draft) => (draft.pitchSemitones = Math.round(value)), 'semitones'),
  });
  const cents = valueStepper({
    label: 'Fine pitch',
    value: computed(() => edit.value?.pitchCents ?? 0),
    min: -100,
    max: 100,
    unit: '¢',
    onChange: (value) => target.write('Fine Pitch', (draft) => (draft.pitchCents = Math.round(value)), 'cents'),
  });
  const speed = valueStepper({
    label: 'Speed',
    value: computed(() => edit.value?.playbackRate ?? 1),
    min: 0.25,
    max: 4,
    step: 0.05,
    format: (value) => `${value.toFixed(2)}×`,
    onChange: (value) => target.write('Speed', (draft) => (draft.playbackRate = value), 'rate'),
  });
  const fadeIn = valueStepper({
    label: 'Fade in',
    value: computed(() => edit.value?.fadeInDuration ?? 0),
    min: 0,
    max: Math.max(0.5, asset.duration),
    step: 0.01,
    format: (value) => `${value.toFixed(2)} s`,
    onChange: (value) => target.write('Fade In', (draft) => (draft.fadeInDuration = value), 'fadeIn'),
  });
  const fadeOut = valueStepper({
    label: 'Fade out',
    value: computed(() => edit.value?.fadeOutDuration ?? 0),
    min: 0,
    max: Math.max(0.5, asset.duration),
    step: 0.01,
    format: (value) => `${value.toFixed(2)} s`,
    onChange: (value) => target.write('Fade Out', (draft) => (draft.fadeOutDuration = value), 'fadeOut'),
  });
  const crossfade = valueStepper({
    label: 'Crossfade',
    value: computed(() => edit.value?.loopCrossfade ?? 0),
    min: 0,
    max: 0.5,
    step: 0.005,
    format: (value) => `${(value * 1000).toFixed(0)} ms`,
    onChange: (value) => target.write('Loop Crossfade', (draft) => (draft.loopCrossfade = value), 'crossfade'),
  });

  /**
   * The amplitude envelope.
   *
   * `SampleEdit.envelope` has been in the model and honoured by the renderer all along, and
   * nothing in the UI ever wrote it — `grep envelope src/ui` returned only comments. These
   * four are that control. `envelopeWrite` creates the envelope on first touch and removes
   * it again when it is back to a straight-through shape, so a sample that does not use one
   * does not pay for a render pass it does not need.
   */
  const IDENTITY_ENVELOPE: AmpEnvelope = { attack: 0, decay: 0, sustain: 1, release: 0 };

  function envelopeValue(key: keyof AmpEnvelope): number {
    return edit.value?.envelope?.[key] ?? IDENTITY_ENVELOPE[key];
  }

  function envelopeWrite(label: string, key: keyof AmpEnvelope, value: number, coalesce: string): void {
    edits.write(
      label,
      (draft) => {
        const next: AmpEnvelope = { ...(draft.envelope ?? IDENTITY_ENVELOPE), [key]: value };
        const isIdentity =
          next.attack === 0 && next.decay === 0 && next.sustain === 1 && next.release === 0;
        if (isIdentity) delete draft.envelope;
        else draft.envelope = next;
        return draft;
      },
      coalesce,
    );
  }

  const attack = valueStepper({
    label: 'Attack',
    value: computed(() => envelopeValue('attack')),
    min: 0,
    max: 5,
    step: 0.01,
    format: (value) => `${value.toFixed(2)} s`,
    onChange: (value) => envelopeWrite('Attack', 'attack', value, 'attack'),
  });
  const decay = valueStepper({
    label: 'Decay',
    value: computed(() => envelopeValue('decay')),
    min: 0,
    max: 5,
    step: 0.01,
    format: (value) => `${value.toFixed(2)} s`,
    onChange: (value) => envelopeWrite('Decay', 'decay', value, 'decay'),
  });
  const sustain = valueStepper({
    label: 'Sustain',
    value: computed(() => envelopeValue('sustain')),
    min: 0,
    max: 1,
    step: 0.02,
    format: (value) => `${Math.round(value * 100)}%`,
    onChange: (value) => envelopeWrite('Sustain', 'sustain', value, 'sustain'),
  });
  const release = valueStepper({
    label: 'Release',
    value: computed(() => envelopeValue('release')),
    min: 0,
    max: 5,
    step: 0.01,
    format: (value) => `${value.toFixed(2)} s`,
    onChange: (value) => envelopeWrite('Release', 'release', value, 'release'),
  });

  const curveRail = segmentedRail({
    items: FADE_CURVES.map((curve) => ({ id: curve, title: fadeCurveDisplayName(curve) })),
    selection: computed(() => edit.value?.fadeCurve ?? 'equalPower'),
    label: 'Fade curve',
    onSelect: (id) => target.write('Fade Curve', (draft) => (draft.fadeCurve = id as FadeCurve)),
  });

  // --- Effect rack ---------------------------------------------------------------------

  const rack = h('div', { class: 'rack' });
  const stopRack = effect(() => {
    const current = edit.value;
    rack.replaceChildren();
    if (current === undefined) return;
    if (current.chain.nodes.length === 0) {
      rack.appendChild(h('p', { class: 'muted' }, 'No effects yet. Add one below. Every parameter is live, and A/B compares against the untouched original.'));
      return;
    }
    // `EffectChain.isBypassed` is in the model and honoured by both renderers, and the only
    // control that wrote it was removed because it was mislabelled as an A/B. This is the
    // control the brief asks for — bypass the *whole* chain, alongside the per-node
    // switches — and it says what it does: a saved property of the sound, not an audition.
    rack.appendChild(
      toggle({
        title: 'Bypass every effect',
        subtitle: 'Silences the whole rack at once and keeps the settings. Saved with the sound.',
        on: computed(() => edit.value?.chain.isBypassed ?? false),
        onChange: (on) => target.write('Bypass Chain', (draft) => (draft.chain.isBypassed = on)),
      }).el,
    );
    current.chain.nodes.forEach((node, index) => rack.appendChild(rackRow(node, index, current.chain.nodes.length)));
  });
  onCleanup(rack, stopRack);

  function rackRow(node: EffectNode, index: number, total: number): HTMLElement {
    const params = effectKindParameters(node.kind).map(
      (descriptor) =>
        parameterRow({
          descriptor,
          value: node.values[descriptor.key] ?? descriptor.defaultValue,
          enabled: !node.isBypassed,
          onChange: (value) =>
            edits.write(
              `${effectKindDisplayName(node.kind)} ${descriptor.name}`,
              (draft) => {
                const live = draft.chain.nodes.find((entry) => entry.id === node.id);
                if (live !== undefined) live.values[descriptor.key] = value;
              },
              `fx-${node.id}-${descriptor.key}`,
            ),
        }).el,
    );
    return h(
      'div',
      { class: ['rack-node', node.isBypassed ? 'rack-node--bypassed' : ''] },
      h(
        'div',
        { class: 'rack-head' },
        icon(effectKindSymbolName(node.kind), { size: 17 }),
        h('span', { class: 'rack-name' }, effectKindDisplayName(node.kind)),
        iconButton({
          symbol: 'chevron.up',
          label: 'Move earlier',
          enabled: index > 0,
          onPress: () =>
            edits.write('Reorder Effects', (draft) => {
              const [moved] = draft.chain.nodes.splice(index, 1);
              if (moved !== undefined) draft.chain.nodes.splice(Math.max(0, index - 1), 0, moved);
            }),
        }).el,
        iconButton({
          symbol: 'chevron.down',
          label: 'Move later',
          enabled: index < total - 1,
          onPress: () =>
            edits.write('Reorder Effects', (draft) => {
              const [moved] = draft.chain.nodes.splice(index, 1);
              if (moved !== undefined) draft.chain.nodes.splice(Math.min(draft.chain.nodes.length, index + 1), 0, moved);
            }),
        }).el,
        iconButton({
          symbol: node.isBypassed ? 'speaker.slash.fill' : 'checkmark.circle.fill',
          label: node.isBypassed ? 'Enable' : 'Bypass',
          active: !node.isBypassed,
          onPress: () =>
            edits.write('Bypass Effect', (draft) => {
              const live = draft.chain.nodes.find((entry) => entry.id === node.id);
              if (live !== undefined) live.isBypassed = !live.isBypassed;
            }),
        }).el,
        iconButton({
          symbol: 'trash.fill',
          label: 'Remove',
          onPress: () =>
            edits.write('Remove Effect', (draft) => {
              draft.chain.nodes = draft.chain.nodes.filter((entry) => entry.id !== node.id);
            }),
        }).el,
      ),
      h('div', { class: 'rack-params' }, ...params),
    );
  }

  // --- Transport for the editor ---------------------------------------------------------

  const playProcessed = async (): Promise<void> => {
    await engine.unlock();
    if (compareOriginal.peek()) {
      const decoded = buffer.peek();
      if (decoded !== null) engine.previewBuffer(decoded);
      return;
    }
    const rendered = await renderCurrent();
    if (rendered !== null) engine.previewBuffer(rendered);
  };

  // The other half of the shared-element transition: the tile or library row the user came
  // from is named as this surface, so the browser morphs one into the other instead of
  // cross-fading the whole page.
  const waveStage = h('div', { class: 'wave-stage instrument' }, view.el);
  const releaseSurface = adoptEditorSurface(waveStage);

  const screen = h(
    'div',
    { class: 'screen screen--wave' },
    screenHeader({
      kicker: computed(() => `${formatPreciseTime(edit.value?.startTime ?? 0)} → ${formatPreciseTime(edit.value?.endTime ?? asset.duration)}`),
      title: asset.name,
      actions: [
        // Every button on this screen edits the document, four of them destructively, and
        // the only route back was ⌘Z — a keystroke the target device does not have. Undo and
        // Redo belong on the screen that makes the mess.
        ...historyButtons(),
        iconButton({ symbol: 'play.fill', label: 'Preview', onPress: () => void playProcessed() }).el,
        iconButton({ symbol: 'stop.fill', label: 'Stop', onPress: () => engine.stopPreview() }).el,
      ],
    }),
    waveStage,
    screenBody(
      section(
        'A/B',
        toggle({
          title: 'Preview the original',
          subtitle:
            'Preview plays the imported file with nothing applied. It changes what you hear, not what is saved. The sound keeps every edit.',
          on: compareOriginal,
          onChange: (on) => {
            // An audition switch, and only that. It used to also write `chain.isBypassed`
            // into the document: a persistent, undo-registering edit made by a control the
            // user was told was a comparison. It also read back wrong — the switch was born
            // `false` however the stored chain was set — and it did not do what the subtitle
            // claimed, because `isBypassed` gates the effect rack alone while trim, gain,
            // pitch, speed, reverse and normalise all stayed on. `playProcessed` already
            // plays the raw decoded buffer when this is on, which is the whole feature.
            compareOriginal.value = on;
          },
        }).el,
        toggle({
          title: 'Snap to zero crossings',
          subtitle: 'Places trim and split points where the waveform crosses zero, so edits never click.',
          on: snapEnabled,
          onChange: (on) => {
            snapEnabled.value = on;
          },
        }).el,
      ),
      section('Region', regionActions),
      section('Slice', sliceActions),
      section(
        'Sound',
        h('div', { class: 'control-grid' }, gain.el, pan.el, semitones.el, cents.el, speed.el),
        toggle({
          title: 'Keep the pitch when changing speed',
          subtitle: 'Time-stretches instead of resampling. Renders once, then plays with no extra cost.',
          on: computed(() => edit.value?.preservesPitchWhenStretched ?? false),
          onChange: (on) => edits.write('Preserve Pitch', (draft) => (draft.preservesPitchWhenStretched = on)),
        }).el,
        toggle({
          title: 'Reverse',
          on: computed(() => edit.value?.isReversed ?? false),
          onChange: (on) => edits.write('Reverse', (draft) => (draft.isReversed = on)),
        }).el,
        toggle({
          title: 'Normalise',
          subtitle: 'Lifts the loudest peak to just under full scale.',
          on: computed(() => edit.value?.isNormalised ?? false),
          onChange: (on) => edits.write('Normalise', (draft) => (draft.isNormalised = on)),
        }).el,
      ),
      section('Fades', h('div', { class: 'control-grid' }, fadeIn.el, fadeOut.el), curveRail.el),
      section(
        'Envelope',
        h('div', { class: 'control-grid' }, attack.el, decay.el, sustain.el, release.el),
        h(
          'p',
          { class: 'muted' },
          'Shapes the level over the length of the sound. Left flat, the sample plays exactly as recorded: no attack, no decay, full sustain, no release.',
        ),
      ),
      section(
        'Loop',
        toggle({
          title: 'Loop this sound',
          subtitle: 'Repeats between the loop points until the pad is stopped.',
          on: computed(() => edit.value?.loopEnabled ?? false),
          onChange: (on) =>
            edits.write('Loop', (draft) => {
              draft.loopEnabled = on;
              if (on && draft.loopStart === undefined) {
                draft.loopStart = draft.startTime;
                draft.loopEnd = draft.endTime ?? asset.duration;
              }
            }),
        }).el,
        neonButton({
          title: 'Set loop from selection',
          style: 'secondary',
          enabled: computed(() => selection.value !== null),
          onPress: () => {
            const range = selection.peek();
            if (range === null) return;
            edits.write('Loop Points', (draft) => {
              draft.loopEnabled = true;
              draft.loopStart = place(range.start);
              draft.loopEnd = place(range.end);
            });
          },
        }).el,
        crossfade.el,
      ),
      section(
        'Effects',
        rack,
        neonButton({
          title: 'Add effect',
          style: 'secondary',
          onPress: () =>
            presentMenu(
              'Add an effect',
              EFFECT_KINDS.map((kind) => ({
                title: effectKindDisplayName(kind),
                symbol: effectKindSymbolName(kind),
                onSelect: () => edits.write('Add Effect', (draft) => draft.chain.nodes.push(makeEffectNode(kind))),
              })),
            ),
        }).el,
        neonButton({
          title: 'Bounce to a new sound',
          style: 'primary',
          enabled: computed(() => !busy.value),
          onPress: () => void deriveAsset(`${asset.name} bounce`, (draft) => draft),
        }).el,
      ),
    ),
  );

  onCleanup(screen, () => {
    stopOverlay();
    stopPlayhead();
    releaseSurface();
    engine.stopPreview();
    view.destroy();
    for (const control of [gain, pan, semitones, cents, speed, fadeIn, fadeOut, crossfade, curveRail, attack, decay, sustain, release]) {
      control.destroy();
    }
  });
  return screen;
}
